import React, { useState, useEffect, useCallback, useRef } from "react";
import usePersistedState from '../../hooks/usePersistedState';
import useDebounce from '../../hooks/useDebounce';
import {
    FormControl,
    InputLabel,
    Autocomplete,
    MenuItem,
    Select,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Typography,
} from "@mui/material";
import InvoiceWithTotalsTable from "../../components/InvoiceWithTotalsTable";
import useClientService from '../../hooks/useClientService';
import useProviderService from '../../hooks/useProviderService';
import useBuildingService from '../../hooks/useBuildingService';
import useInvoiceService from '../../hooks/useInvoiceService';
import { useSnackbar } from '../../components/SnackbarProvider';
import { handleApiError, DatePickerComponent } from '../../components/Utils';
import dayjs from 'dayjs';
import "dayjs/locale/es";
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

function ListInvoices() {
    const { getClients } = useClientService();
    const { getProviders } = useProviderService();
    const { getBuildings } = useBuildingService();
    const { exportInvoices } = useInvoiceService();
    const showSnackbar = useSnackbar();

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const [apply, setApply] = useState(false);
    const [reset, setReset] = useState(false);

    const [status, setStatus] = useState(null);
    const [code, setCode] = usePersistedState('list_invoices_code', null);
    const [externalProviderId, setExternalProviderId] = usePersistedState('list_invoices_externalProviderId', null);
    const [startInvoiceDate, setStartInvoiceDate] = useState(null);
    const [endInvoiceDate, setEndInvoiceDate] = useState(null);

    const debouncedCode = useDebounce(code);
    const debouncedExternalProviderId = useDebounce(externalProviderId);

    const [clients, setClients] = useState([]);
    const [selectedClient, setSelectedClient] = useState(null);

    const [providers, setProviders] = useState([]);
    const [selectedProvider, setSelectedProvider] = useState(null);

    const [buildings, setBuildings] = useState([]);
    const [selectedBuilding, setSelectedBuilding] = useState(null);

    const isFirstFetch = useRef(true);

    const fetchClients = useCallback(async () => {
        try {
            if (isFirstFetch.current) setLoading(true);
            const result = await getClients({});
            setClients(result.data.items);
            isFirstFetch.current = false;
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar los clientes.'));
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchProviders = useCallback(async () => {
        try {
            const result = await getProviders({});
            setProviders(result.data.items);
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar los proveedores.'));
        }
    }, []);

    const fetchBuildings = useCallback(async () => {
        try {
            const result = await getBuildings({});
            setBuildings(result.data.items);
        } catch (error) {
            setError(handleApiError(error, 'Error al cargar las obras.'));
        }
    }, []);

    useEffect(() => {
        fetchClients();
        fetchProviders();
        fetchBuildings();
    }, []);

    useEffect(() => {
        setApply(true);
        setTimeout(() => setApply(false), 0);
    }, [debouncedCode, debouncedExternalProviderId, status, selectedClient, selectedProvider, selectedBuilding, startInvoiceDate, endInvoiceDate]);

    const handleStatusChange = (event) => {
        const { value } = event.target;
        setStatus(value);
    };

    const handleResetFilters = () => {
        setStatus(null);
        setSelectedClient(null);
        setSelectedBuilding(null);
        setSelectedProvider(null);
        setCode(null);
        setExternalProviderId(null);
        setReset(true);
        setStartInvoiceDate(null);
        setEndInvoiceDate(null);
        setTimeout(() => setReset(false), 0);
    };

    const handleClientChange = (event, newValue) => {
        setSelectedClient(newValue);
    };

    const handleProviderChange = (event, newValue) => {
        setSelectedProvider(newValue);
    };

    const handleBuildingChange = (event, newValue) => {
        setSelectedBuilding(newValue);
    };

    const buildExportParams = () => {
        const params = {};
        if (code) params.code = code;
        if (externalProviderId) params.external_provider_id = externalProviderId;
        if (status) params.invoice_status = status;
        if (selectedBuilding) params.building_id = selectedBuilding.id;
        if (selectedClient) params.client_id = selectedClient.id;
        if (selectedProvider) params.provider_id = selectedProvider.id;
        if (startInvoiceDate) {
            const startDateMidday = dayjs(startInvoiceDate).hour(12).minute(0).second(0);
            params.start_invoice_date = startDateMidday.utc().format('YYYY-MM-DD');
        }
        if (endInvoiceDate) {
            const endDateMidday = dayjs(endInvoiceDate).hour(12).minute(0).second(0);
            params.end_invoice_date = endDateMidday.utc().format('YYYY-MM-DD');
        }
        return params;
    };

    const generateTimestampFilename = (extension) => {
        const now = new Date();
        const year = now.getFullYear();
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const day = String(now.getDate()).padStart(2, '0');
        const hours = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');
        const seconds = String(now.getSeconds()).padStart(2, '0');
        return `Facturas_${year}-${month}-${day}_${hours}:${minutes}:${seconds}.${extension}`;
    };

    const downloadBlob = (data, mimeType, filename) => {
        const blob = new Blob([data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const handleDateChange = (newValue, name) => {
        if (name === 'start') {
            setStartInvoiceDate(newValue);
        } else if (name === 'end') {
            setEndInvoiceDate(newValue);
        }
    };


    return (
        <div>
            {loading && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                    <CircularProgress />
                </div>
            )}
            {!loading && (
                <>
                    {/* Filters and Sorting */}
                    <Grid container spacing={1} alignItems="center" sx={{ marginTop: '80px' }}>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} md={9}>
                                <Grid container spacing={1} alignItems="center">
                                    {/* Código */}
                                    <Grid item xs={3} sm={2}>
                                        <TextField
                                            fullWidth
                                            label="Código"
                                            name="code"
                                            value={code || ""}
                                            onChange={(e) => setCode(e.target.value)}
                                        />
                                    </Grid>
                                    {/* Código Externo */}
                                    <Grid item xs={3} sm={2}>
                                        <TextField
                                            fullWidth
                                            label="Código externo"
                                            name="external_provider_id"
                                            value={externalProviderId || ""}
                                            onChange={(e) => setExternalProviderId(e.target.value)}
                                        />
                                    </Grid>
                                    {/* Obra */}
                                    <Grid item sm={2}>
                                        <Autocomplete
                                            options={buildings}
                                            getOptionLabel={(option) => option.code?.toString() + '-' + option.name?.toString()}
                                            value={selectedBuilding}
                                            onChange={handleBuildingChange}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Obra" style={{ maxWidth: '100%' }} />
                                            )}
                                        />
                                    </Grid>
                                    {/* Cliente */}
                                    <Grid item sm={2}>
                                        <Autocomplete
                                            options={clients}
                                            getOptionLabel={(option) => option.code?.toString() + '-' + option.name?.toString()}
                                            value={selectedClient}
                                            onChange={handleClientChange}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Cliente" style={{ maxWidth: '100%' }} />
                                            )}
                                        />
                                    </Grid>
                                    {/* Proveedores */}
                                    <Grid item sm={2}>
                                        <Autocomplete
                                            options={providers}
                                            getOptionLabel={(option) => option.code?.toString() + '-' + option.name?.toString()}
                                            value={selectedProvider}
                                            onChange={handleProviderChange}
                                            renderInput={(params) => (
                                                <TextField {...params} label="Proveedores" style={{ maxWidth: '100%' }} />
                                            )}
                                        />
                                    </Grid>
                                    {/* Estado */}
                                    <Grid item xs={3} sm={2}>
                                        <FormControl fullWidth>
                                            <InputLabel id="status-label">Estado</InputLabel>
                                            <Select
                                                labelId="status-label"
                                                id="status"
                                                name="status"
                                                value={status}
                                                onChange={handleStatusChange}
                                                label="Tipo"
                                            >
                                                <MenuItem value="paid">PAGADA</MenuItem>
                                                <MenuItem value="not_paid">NO PAGADA</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {/* Botones alineados a la derecha */}
                            <Grid item xs={12} md={3}>
                                <Grid container spacing={1} justifyContent="flex-end">
                                    <Grid item>
                                        <Button variant="contained" color="secondary" onClick={handleResetFilters}>
                                            Limpiar Filtros
                                        </Button>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>


                        {/* Filtros de Fecha */}
                        <Grid container spacing={1} alignItems="center" sx={{ marginTop: '10px' }}>
                            <Grid item xs={12} md={9}>
                                <Grid container spacing={1} alignItems="center">
                                    <Grid item>
                                        <DatePickerComponent
                                            label="Fecha de Inicio"
                                            value={startInvoiceDate}
                                            onChange={(newValue) => handleDateChange(newValue, 'start')}
                                            fullWidth={false}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <DatePickerComponent
                                            label="Fecha de Fin"
                                            value={endInvoiceDate}
                                            onChange={(newValue) => handleDateChange(newValue, 'end')}
                                            fullWidth={false}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    <br />
                    <br />
                    {/* Client list */}
                    <InvoiceWithTotalsTable
                        title={'Facturas de clientes'}
                        isClient={true}
                        code={code}
                        status={status}
                        selectedBuilding={selectedBuilding}
                        externalProviderId={externalProviderId}
                        selectedClient={selectedClient}
                        startInvoiceDate={startInvoiceDate}
                        endInvoiceDate={endInvoiceDate}
                        apply={apply}
                        reset={reset}
                    />
                    <br />
                    <br />
                    <br />
                    {/* Provider list */}
                    <InvoiceWithTotalsTable
                        title={'Facturas de proveedores'}
                        isClient={false}
                        code={code}
                        status={status}
                        selectedBuilding={selectedBuilding}
                        externalProviderId={externalProviderId}
                        selectedProvider={selectedProvider}
                        startInvoiceDate={startInvoiceDate}
                        endInvoiceDate={endInvoiceDate}
                        apply={apply}
                        reset={reset}
                    />
                </>
            )
            }
            <br />
            {
                error && (
                    <Typography variant="body2" color="error" style={{ marginTop: 10 }}>
                        {error}
                    </Typography>
                )
            }
        </div >
    );
}

export default ListInvoices;
